import { Injectable, Inject, Optional } from '@angular/core';
import { HttpInterceptor, HttpHandler, HttpRequest, HttpErrorResponse, HttpEvent } from '@angular/common/http';
import { BehaviorSubject, from, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { SessionService } from '../services/session.service';
import { RoutingService } from '../services/routing.service';
import { SessionStorageKeys } from '../enum/session-storage-keys';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  private refreshInProgress = false;
  private refreshSubject = new BehaviorSubject<string | null>(null);

  constructor(
    @Optional() @Inject('serverUrl') protected serverUrl: string,
    private sessionSvc: SessionService,
    private router: RoutingService
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const authInfoString = sessionStorage.getItem(SessionStorageKeys.SESSION);
    const authInfo = authInfoString ? JSON.parse(authInfoString) : null;
    const token = authInfo?.auth?.token;

    let clonedRequest = req;
    if (token) {
      clonedRequest = this.addToken(req, token);
    }

    return next.handle(clonedRequest).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401 && authInfo?.auth?.refreshToken) {
          return this.handleRefreshToken(req, next);
        }
        return throwError(() => error);
      })
    );
  }

  private addToken(request: HttpRequest<any>, token: string): HttpRequest<any> {
    let cloned = request.clone({
      headers: request.headers.set('Authorization', token || '')
    });

    if (this.serverUrl) {
      cloned = cloned.clone({ url: `${this.serverUrl}${request.url}` });
    }

    return cloned;
  }

  private handleRefreshToken(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (this.refreshInProgress) {
      return this.refreshSubject.pipe(
        filter(token => token != null),
        take(1),
        switchMap(token => next.handle(this.addToken(req, token!)))
      );
    }

    this.refreshInProgress = true;

    return from(this.sessionSvc.refreshSession()).pipe(
      switchMap(token => {
        this.refreshInProgress = false;
        this.refreshSubject.next(token);
        return next.handle(this.addToken(req, token));
      }),
      catchError(err => {
        this.refreshInProgress = false;
        this.refreshSubject.next(null);
        this.handleLogout();
        return throwError(() => err);
      })
    );
  }

  private handleLogout() {
    sessionStorage.removeItem(SessionStorageKeys.SESSION);
    this.router.goLogin();
  }
}
